import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import DealCard from "../components/DealCard";
import { ArrowLeft, Store, Heart } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

// Try to derive a Clearbit-friendly hostname from any deal URL for this merchant
function hostFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    return null;
  }
}

export default function Merchant() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name || "");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const [dealsRes, countRes, followingRes] = await Promise.all([
          api.get("/deals", { params: { merchant: decoded } }),
          api.get(`/merchants/${encodeURIComponent(decoded)}/followers`),
          api.get(`/merchants/${encodeURIComponent(decoded)}/following`),
        ]);
        setDeals(dealsRes.data || []);
        setFollowerCount(countRes.data?.followers || 0);
        setFollowing(!!followingRes.data?.following);
      } finally {
        setLoading(false);
      }
    })();
  }, [decoded]);

  const toggleFollow = async () => {
    if (!user) {
      toast.error("Log in to follow merchants.");
      navigate("/login");
      return;
    }
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/merchants/${encodeURIComponent(decoded)}/follow`);
      const isFollowing = data.action === "followed";
      setFollowing(isFollowing);
      setFollowerCount((n) => Math.max(0, n + (isFollowing ? 1 : -1)));
      toast.success(
        isFollowing
          ? `Following ${decoded} — we'll email you every new deal.`
          : `Unfollowed ${decoded}.`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFollowBusy(false);
    }
  };

  const logoHost = useMemo(() => {
    for (const d of deals) {
      const h = hostFromUrl(d.url);
      if (h) return h;
    }
    return null;
  }, [deals]);

  const categories = useMemo(() => {
    const set = new Set(deals.map((d) => d.category).filter(Boolean));
    return Array.from(set);
  }, [deals]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid={`merchant-page-${decoded}`}>
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]"
        data-testid="merchant-back-link"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to home
      </Link>

      <div className="mt-6 mb-10 flex items-start gap-6 border-b border-black/10 pb-8">
        <div className="w-20 h-20 flex-shrink-0 border border-black/10 bg-white flex items-center justify-center" data-testid="merchant-logo">
          {logoHost ? (
            <img
              src={`https://logo.clearbit.com/${logoHost}?size=128`}
              alt={decoded}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div className="hidden w-full h-full items-center justify-center bg-black/5" style={{ display: logoHost ? "none" : "flex" }}>
            <Store className="w-8 h-8 text-black/40" strokeWidth={1.5} />
          </div>
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-2">
            Merchant &middot; {deals.length} {deals.length === 1 ? "deal" : "deals"}
            {followerCount > 0 && (
              <>
                {" "}&middot; <span data-testid="merchant-follower-count">{followerCount} follower{followerCount === 1 ? "" : "s"}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter" data-testid="merchant-name">
              {decoded}
            </h1>
            <button
              type="button"
              onClick={toggleFollow}
              disabled={followBusy}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] border-2 transition-colors disabled:opacity-50 ${
                following
                  ? "bg-[#111111] text-white border-[#111111] hover:bg-[#E63946] hover:border-[#E63946]"
                  : "bg-white text-black border-black hover:bg-black hover:text-white"
              }`}
              data-testid="merchant-follow-btn"
            >
              <Heart className="w-3.5 h-3.5" strokeWidth={2} fill={following ? "currentColor" : "none"} />
              {following ? "Following" : "Follow"}
            </button>
          </div>
          {following && (
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-black/50" data-testid="merchant-follow-hint">
              We&rsquo;ll email you the moment {decoded} posts a new deal.
            </div>
          )}
          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="merchant-categories">
              {categories.map((c) => (
                <Link
                  key={c}
                  to={`/category/${c}`}
                  className="text-[10px] font-bold uppercase tracking-widest border border-black/20 px-2.5 py-1 hover:border-black hover:bg-black hover:text-white transition-colors"
                >
                  {c}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-black/40 font-mono uppercase tracking-widest text-xs">Loading…</div>
      ) : deals.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="merchant-empty">
          <div className="font-serif text-3xl mb-2">No active deals</div>
          <p className="text-black/60 text-sm">We aren&rsquo;t tracking any live offers from {decoded} right now. Check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="merchant-deals-grid">
          {deals.map((d) => (
            <DealCard key={d.id} deal={d} />
          ))}
        </div>
      )}
    </div>
  );
}
